/**
 * backend #693: which culture this app formats numbers and dates with.
 *
 * The pairing is decided once, in the backend's `SupportedLanguages.FormattingCulture`, and this
 * file mirrors it. It is not a local preference: the same order appears in a confirmation mail
 * rendered by the server and on the screen the user ordered from, and the two must not write its
 * date differently.
 *
 * The decision, in short. A language tag is not a formatting culture. Bare `en` is US English, so
 * it writes `9/7/2026` for the seventh of September; every English variant agrees on `1,234.50`,
 * so the *only* thing in question is the date. This is a European marketplace whose English
 * readers are overwhelmingly here, and to them month-first does not read as unfamiliar, it reads
 * as the wrong day. Hence `en-GB` — a slight fiction, since none of this is British, and the
 * closest standard locale to "European English".
 *
 * German maps to Angular's `de`, which *is* CLDR German — there is no separate `de-DE` locale file
 * because the two are identical. The backend names it `de-DE`; same behaviour, same output. What
 * the pinning spec asserts is the rendered result rather than the tag, which is the part that has
 * to agree across repos.
 *
 * Currency is never taken from the locale: amounts carry their own ISO code, so a EUR order is
 * never shown with a pound sign.
 */

/** The house default, matching the backend's `SupportedLanguages.Default`. */
export const DEFAULT_LANGUAGE = 'de';

/** language → the locale its numbers and dates are formatted with. */
export const FORMATTING_LOCALES: Readonly<Record<string, string>> = Object.freeze({
  de: 'de',
  en: 'en-GB',
});

/**
 * The bare language tag of a locale, folding a region variant to its primary subtag
 * (`en-GB` → `en`, `de-DE` → `de`) exactly as the backend's `SupportedLanguages.Normalize` does.
 * Anything unsupported, empty or missing is the house default.
 *
 * This is what a *language* is sent as — `Accept-Language`, `ui_locales`, the `Language` setting,
 * `<html lang>`. It is deliberately not the same value as {@link formattingLocaleFor}: `LOCALE_ID`
 * is `en-GB` in the English bundle, and the platform's language tags are bare.
 */
export function languageFor(locale: string | null | undefined): string {
  const primary = (locale ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return primary in FORMATTING_LOCALES ? primary : DEFAULT_LANGUAGE;
}

/**
 * The formatting locale for a language tag, folding a region variant to its primary subtag
 * (`en-US` → `en` → `en-GB`) exactly as the backend's `SupportedLanguages.Normalize` does.
 * Anything unsupported, empty or missing formats as the house default.
 */
export function formattingLocaleFor(language: string | null | undefined): string {
  return FORMATTING_LOCALES[languageFor(language)];
}

/**
 * The language this bundle was compiled for. `$localize.locale` is set by a localized build; under
 * `ng serve` without `localize` there is none, and the default is the honest answer.
 */
export function buildLanguage(): string {
  const localize = (globalThis as { $localize?: { locale?: string } }).$localize;
  return localize?.locale ?? DEFAULT_LANGUAGE;
}

/** The `LOCALE_ID` this bundle should format with. */
export function buildFormattingLocale(): string {
  return formattingLocaleFor(buildLanguage());
}
